import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import WarrantyFormShippingOptions from '../Fields/WarrantyFormShippingOptions';

// Mapping functions for enum values to human-readable text
function mapScanStatus(value) {
  const mapping = {
    no_new_scan: 'No new scan after last retainer shipment',
    new_scan: 'New scan taken since last retainer shipment',
  };
  return mapping[value] || value;
}

function mapHaveCurrentSet(value) {
  if (value === true || value === 'true') {
    return 'Yes';
  }
  if (value === false || value === 'false') {
    return 'No';
  }
  return 'Not provided';
}

function mapHowOftenWear(value) {
  const mapping = {
    every_night: 'Every Night',
    a_few_times_per_week: 'A few times per week',
    daily: 'Daily (day and night)',
    as_needed: 'As needed',
    rarely: 'Rarely',
  };
  return mapping[value] || value;
}

function mapUserIssue(value) {
  const mapping = {
    does_not_fit: "Doesn't fit properly",
    manufacturing_issue: 'Manufacturing Issue / Defect',
  };
  return mapping[value] || value;
}

function mapArch(value) {
  const mapping = {
    upper: 'Top retainer',
    lower: 'Bottom retainer',
    both: 'Both retainers',
  };
  return mapping[value] || value;
}

function mapArchFit(value) {
  const mapping = {
    upper_too_tight: 'Too tight',
    upper_too_loose: 'Too loose',
    upper_rubs_on_gums: 'Rubs on gums',
    upper_other: 'Other (Include specific notes in next section)',
    lower_too_tight: 'Too tight',
    lower_too_loose: 'Too loose',
    lower_rubs_on_gums: 'Rubs on gums',
    lower_other: 'Other (Include specific notes in next section)',
  };
  return mapping[value] || value;
}

function mapWhereToShip(value) {
  const mapping = {
    partner_location: 'Orthodontic office',
    patient_address: "Patient's home address",
  };
  return mapping[value] || value;
}

function StepFour({ productionOrder }) {
  const { getValues } = useFormContext();
  const formValues = getValues();

  // Get patient information from production order
  const patient = productionOrder?.patient;
  const patientName = patient
    ? `${patient.firstName} ${patient.lastName}`
    : 'Not available';
  const orderId = productionOrder?.hexCode || 'Not available';

  // Get warranty image count
  const imageCount = formValues.warrantyImagesAttributes?.length || 0;
  const photosUploaded = imageCount > 0 ? 'Yes' : 'No';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-bold text-gray-900 text-xl leading-6">
          Review & Submit
        </h3>
        <p className="mt-1 text-gray-500 text-sm">
          Please review your information before submitting
        </p>
      </div>

      {/* Shipping Preference */}
      <WarrantyFormShippingOptions />

      {/* Condensed Request Summary */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-left">
        <div className="space-y-3">
          <h3 className="mb-4 font-bold text-gray-900 text-lg leading-6">
            Request Summary
          </h3>
          <div className="flex">
            <dt className="w-1/3 font-bold text-gray-900 text-sm">Patient:</dt>
            <dd className="text-gray-900 text-sm">{patientName}</dd>
          </div>

          <div className="flex">
            <dt className="w-1/3 font-bold text-gray-900 text-sm">Order ID:</dt>
            <dd className="text-gray-900 text-sm">{orderId}</dd>
          </div>

          <div className="flex">
            <dt className="w-1/3 font-bold text-gray-900 text-sm">
              Issue Type:
            </dt>
            <dd className="text-gray-900 text-sm">
              {mapUserIssue(formValues.userIssue) || 'Not provided'}
            </dd>
          </div>

          <div className="flex">
            <dt className="w-1/3 font-bold text-gray-900 text-sm">
              Affected Retainer:
            </dt>
            <dd className="text-gray-900 text-sm">
              {mapArch(formValues.arch) || 'Not provided'}
            </dd>
          </div>

          <div className="flex">
            <dt className="w-1/3 font-bold text-gray-900 text-sm">
              Recent 3D Scan:
            </dt>
            <dd className="text-gray-900 text-sm">
              {mapScanStatus(formValues.scanStatus) || 'Not provided'}
            </dd>
          </div>

          <div className="flex">
            <dt className="w-1/3 font-bold text-gray-900 text-sm">
              Current Retainers Available:
            </dt>
            <dd className="text-gray-900 text-sm">
              {mapHaveCurrentSet(formValues.haveCurrentSet)}
            </dd>
          </div>

          <div className="flex">
            <dt className="w-1/3 font-bold text-gray-900 text-sm">
              Wear Frequency:
            </dt>
            <dd className="text-gray-900 text-sm">
              {mapHowOftenWear(formValues.howOftenWear) || 'Not provided'}
            </dd>
          </div>

          <div className="flex">
            <dt className="w-1/3 font-bold text-gray-900 text-sm">
              Shipping to:
            </dt>
            <dd className="text-gray-900 text-sm">
              {mapWhereToShip(formValues.whereToShip) || "Patient's home"}
            </dd>
          </div>

          {/* Conditional arch fit information */}
          {formValues.arch === 'upper' && (
            <div className="flex">
              <dt className="w-1/3 font-bold text-gray-900 text-sm">
                Top Arch Issue:
              </dt>
              <dd className="text-gray-900 text-sm">
                {mapArchFit(formValues.upperArchFit) || 'Not provided'}
              </dd>
            </div>
          )}

          {formValues.arch === 'lower' && (
            <div className="flex">
              <dt className="w-1/3 font-bold text-gray-900 text-sm">
                Bottom Arch Issue:
              </dt>
              <dd className="text-gray-900 text-sm">
                {mapArchFit(formValues.lowerArchFit) || 'Not provided'}
              </dd>
            </div>
          )}

          {formValues.arch === 'both' && (
            <>
              <div className="flex">
                <dt className="w-1/3 font-bold text-gray-900 text-sm">
                  Top Arch Issue:
                </dt>
                <dd className="text-gray-900 text-sm">
                  {mapArchFit(formValues.upperArchFit) || 'Not provided'}
                </dd>
              </div>
              <div className="flex">
                <dt className="w-1/3 font-bold text-gray-900 text-sm">
                  Bottom Arch Issue:
                </dt>
                <dd className="text-gray-900 text-sm">
                  {mapArchFit(formValues.lowerArchFit) || 'Not provided'}
                </dd>
              </div>
            </>
          )}

          <div className="flex">
            <dt className="w-1/3 font-bold text-gray-900 text-sm">
              Photos Uploaded:
            </dt>
            <dd className="text-gray-900 text-sm">{photosUploaded}</dd>
          </div>

          {/* Additional Details */}
          {formValues.message && (
            <div className="flex">
              <dt className="w-1/3 font-bold text-gray-900 text-sm">
                Additional Details:
              </dt>
              <dd className="text-gray-900 text-sm">{formValues.message}</dd>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

StepFour.propTypes = {
  productionOrder: PropTypes.object,
};

export default StepFour;
